import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAppSelector } from '@/hooks';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import { ChatIcon, SettingsIcon, type IconProps } from '@/components/UI/icons';
import { tenantNavItems as navItems, platformNavItems as platformAdminItems } from '@/components/Layout/navItems';

interface SidebarProps {
  /** Called when a nav item is tapped or the close button is pressed (mobile). */
  onClose?: () => void;
}

/** Icons for the entries that have one. The long platform list stays text-only. */
const NAV_ICONS: Record<string, React.FC<IconProps>> = {
  '/chat': ChatIcon,
  '/settings/tenant': SettingsIcon,
};

// The sidebar is always-dark branded chrome in both colour modes, so it reads the `chrome` tokens rather
// than the light/dark page tokens (which is what left its links unreadable in light mode).
const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'group flex items-center gap-3 rounded-full px-3.5 py-2.5 text-sm font-medium transition-colors',
    isActive ? 'bg-white/[0.12] text-chrome-text' : 'text-chrome-text2 hover:bg-white/10 hover:text-chrome-text',
  );

const labelClass =
  'flex cursor-default select-none items-center gap-3 rounded-full px-3.5 py-2.5 text-sm font-medium text-chrome-icon';

const NavRow: React.FC<{ to: string; label: string; onClick?: () => void }> = ({ to, label, onClick }) => {
  const Icon = NAV_ICONS[to];
  return (
    <NavLink to={to} className={linkClass} onClick={onClick}>
      {({ isActive }) => (
        <>
          {Icon && (
            <Icon
              size={20}
              className={cn(
                'shrink-0 transition-colors',
                isActive ? 'text-chrome-primary' : 'text-chrome-icon group-hover:text-chrome-text2',
              )}
            />
          )}
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const roles = useAppSelector((s) => s.sessionState.roles);
  const isPlatformRole = roles.includes('platform_admin') || roles.includes('platform_support');

  // The sidebar always shows the RoofVox product brand (lockup from LeftSidePanel) for every user, tenant or
  // platform. Tenant company name + logo are shown in the top bar instead — not here.
  return (
    <LeftSidePanel
      width={280}
      footer={
        !isPlatformRole ? <NavRow to="/settings/tenant" label="Settings" onClick={onClose} /> : null
      }
    >
      {isPlatformRole ? (
        <div className="space-y-1">
          {platformAdminItems.map((item) => (
            <NavRow key={item.path} to={item.path} label={item.label} onClick={onClose} />
          ))}
        </div>
      ) : (
        <div className="space-y-1">
          {navItems.map((item) =>
            item.path ? (
              <NavRow key={item.path} to={item.path} label={item.label} onClick={onClose} />
            ) : (
              <div key={item.label} className={labelClass} aria-disabled="true">
                {item.label}
              </div>
            ),
          )}
        </div>
      )}
    </LeftSidePanel>
  );
};

export default Sidebar;
